import { useEffect, useState } from "react";
import { get, BASE_URL } from "../../utils/service";

const LABELS: Record<string, string> = { google: "Google", microsoft: "Microsoft" };

/** Where the backend's browser OAuth routes live (same host as the API, outside /api/v1). */
export const oauthUrl = (provider: string, query: Record<string, string> = {}) => {
  const origin = /^https?:\/\//.test(BASE_URL) ? new URL(BASE_URL).origin : window.location.origin;
  const qs = new URLSearchParams(query).toString();
  return `${origin}/auth/oauth/${provider}/redirect${qs ? `?${qs}` : ""}`;
};

function ProviderLogo({ provider }: { provider: string }) {
  if (provider === "google") {
    return (
      <svg viewBox="0 0 48 48" className="h-4 w-4" aria-hidden="true">
        <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z" />
        <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z" />
        <path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-7.9l-6.5 5C9.5 39.6 16.2 44 24 44z" />
        <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C37 39.2 44 34 44 24c0-1.3-.1-2.4-.4-3.5z" />
      </svg>
    );
  }
  return (
    <svg viewBox="0 0 23 23" className="h-4 w-4" aria-hidden="true">
      <path fill="#f35325" d="M1 1h10v10H1z" /><path fill="#81bc06" d="M12 1h10v10H12z" /><path fill="#05a6f0" d="M1 12h10v10H1z" /><path fill="#ffba08" d="M12 12h10v10H12z" />
    </svg>
  );
}

/**
 * "Continue with Google / Microsoft" for the providers the system has enabled. Renders nothing
 * when none are. `query` carries the intent (login, or accepting an invitation).
 */
export default function OAuthButtons({ query, label = "Continue with", layout = "stack" }: { query?: Record<string, string>; label?: string; layout?: "stack" | "row" }) {
  const [providers, setProviders] = useState<string[]>([]);
  useEffect(() => {
    get<{ data: { providers: string[] } }>("/auth/oauth/providers").then((r) => setProviders(r.data.providers)).catch(() => setProviders([]));
  }, []);
  if (providers.length === 0) return null;
  const divider = (
    <div className="flex items-center gap-3 text-[11px] text-gray-400">
      <span className="h-px flex-1 bg-gray-200" />
      or
      <span className="h-px flex-1 bg-gray-200" />
    </div>
  );
  // Side by side above the form, then the divider (sign-in page).
  if (layout === "row") {
    return (
      <div className="space-y-6">
        <div className="grid gap-3 sm:grid-cols-2">
          {providers.map((p) => (
            <a key={p} href={oauthUrl(p, query ?? { intent: "login" })}
              className="flex items-center justify-center gap-2.5 rounded-full border border-gray-200 bg-white px-4 py-3 text-xs font-medium text-gray-700 transition hover:border-gray-300 hover:bg-gray-50">
              <ProviderLogo provider={p} />
              {label} {LABELS[p] ?? p}
            </a>
          ))}
        </div>
        {divider}
      </div>
    );
  }
  return (
    <div className="space-y-3">
      {divider}
      {providers.map((p) => (
        <a
          key={p}
          href={oauthUrl(p, query ?? { intent: "login" })}
          className="flex w-full items-center justify-center gap-2 rounded-2xl border border-gray-200 bg-white py-3.5 text-xs font-medium text-gray-800 hover:bg-gray-50 transition-colors"
        >
          <ProviderLogo provider={p} />
          {label} {LABELS[p] ?? p}
        </a>
      ))}
    </div>
  );
}
